/* eslint-disable max-classes-per-file */
/**
 * A Payment Method with details to reference and vault it.
 * @typedef {object} PaymentMethodType
 * @property {string} nonce The Nonce reference for the Payment Method to vault and verify against 3DS.
 * @property {string} bin The Bank Identification Number.
 * @property {string} paymentType The type of payment method being used: credit_card, paypal or bank_transfer.
 * @property {string} [cardholderName] The name on the card.
 * @property {string} [countryCode] The country code in 2-letter alphabetical form.
 * @property {string} [city] The city of the cardholder.
 * @property {string} [postCode] the payment method's post-code.
 */
/**
 * @type {PaymentMethodType}
 */
export class PaymentMethod {
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(nonce, bin, paymentType, cardholderName, countryCode, city, postCode) {
    this.nonce = nonce;
    this.bin = bin;
    this.paymentType = paymentType;
    this.cardholderName = cardholderName;
    this.countryCode = countryCode;
    this.city = city;
    this.postCode = postCode;
  }
}

/**
 * @typedef {object} fromVault
 * @property {string} token The token reference to a payment method vaulted in Braintree.
 */

/**
 * A Payment Method after we vault it in Braintree. We include the token reference to the payment method.
 * A vaulted payment method is the result of vaulting a PaymentMethod _or_ retrieving an existing payment method from
 * the backend.
 * Most cases of interacting with the backend directly will require a VaultedPaymentMethod.
 * @typedef {PaymentMethod & fromVault} VaultedPaymentMethodType
 */
/**
 * @type {VaultedPaymentMethodType}
 */
export class VaultedPaymentMethod extends PaymentMethod {
  /**
   * explicitly typing these so typescript can pick them up
   *
   * @param {string=} nonce
   * @param {string=} bin
   * @param {string=} paymentType
   * @param {string=} cardholderName
   * @param {string=} countryCode
   * @param {string=} city
   * @param {string=} postCode
   * @param {string=} token
   */
  // FIXME: Please fix this. You may refer to CFE-266 for more details.
  // eslint-disable-next-line func-params-args/func-params
  constructor(nonce, bin, paymentType, cardholderName, countryCode, city, postCode, token) {
    super(nonce, bin, paymentType, cardholderName, countryCode, city, postCode);
    this.token = token;
  }

  /**
   * Return a verified VaultedPaymentMethod after running through 3DS.
   * @param {string} nonce The new verified nonce.
   * @returns {VaultedPaymentMethod} A copy of this payment method with the updated nonce.
   */
  getVerifiedPaymentMethod(nonce) {
    return new VaultedPaymentMethod(
      nonce,
      this.bin,
      this.paymentType,
      this.cardholderName,
      this.countryCode,
      this.city,
      this.postCode,
      this.token,
    );
  }
}

export function creditPaymentMethod(paymentType) {
  return new PaymentMethod(null, null, paymentType, null, null, null, null);
}

/**
 * Represent an error raised from payment form submission.
 * @typedef {Error} ValidationError
 * @property {string[]} errorFields The names of the card form fields that had an error.
 * @property {string} name The name of the error.
 */
export class ValidationError extends Error {
  constructor(message, errorFields) {
    super(message);
    this.errorFields = errorFields;
    this.name = 'ValidationError';
  }
}
